import { useEffect, useRef } from 'react';
import { clearCurrentCollection, resetGridPreferencesOnProjectChange, setCurrentProject, setNavActiveTab } from '../../services/angular-bridge';
import { useProjectIdFromRoute } from '../useProjectIdFromRoute';
import { resetBatchTagFilterOnProjectChange } from '../../utilities/batchTagFilter';
import type { ProjectDetail } from '../../types/project';

// Common page-mount glue for any React route under /project/:id. Sets the active
// left-nav tab (and optional subtab) and pushes the loaded project into the Angular
// ProjectService so the sidebar/breadcrumb populate correctly.
export function useProjectPageMount(
  project: ProjectDetail | undefined,
  navName: string,
  subname?: string,
) {
  const projectId = useProjectIdFromRoute();

  // Reset grid sort/filter immediately when the project ID changes — mirrors
  // old ProjectDetailController behaviour. Must depend on projectId (from the
  // URL, available synchronously) not on project data (requires a network
  // fetch), so the reset fires before the user can navigate to Browse.
  useEffect(() => {
    resetGridPreferencesOnProjectChange(projectId);
    resetBatchTagFilterOnProjectChange(projectId);
  }, [projectId]);

  // Returning to a project-level page must drop any collection scope left over
  // from browsing a collection's files, so the top search bar reverts from
  // "This collection" to "All collections". The Angular collection view is not
  // torn down by a React unmount, so we clear it explicitly on mount.
  useEffect(() => {
    clearCurrentCollection();
  }, [projectId]);

  // Keyed on project.id, not the project object: websocket progress events patch the
  // ['project', id] cache entry, producing a new object on every tick. Depending on the
  // object would re-broadcast CurrentProjectChanged each time, which makes the Angular
  // WebSocketService tear down and re-create its project/role STOMP subscriptions.
  const projectRef = useRef(project);
  projectRef.current = project;

  useEffect(() => {
    setNavActiveTab(navName, subname);
    const current = projectRef.current;
    if (current) {
      setCurrentProject(current);
    }
  }, [project?.id, navName, subname]);
}
